import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Header from './components/layout/Header';
import StatsStrip from './components/layout/StatsStrip';
import BottomTabs from './components/layout/BottomTabs';
import MapTab from './components/tabs/MapTab';
import RacesTab from './components/tabs/RacesTab';
import RunTab from './components/tabs/RunTab';
import ErrorBoundary from './components/shared/ErrorBoundary';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      // Add network timeout
      networkMode: 'online',
    },
  },
});

export default function App() {
  const [activeTab, setActiveTab] = useState(() => {
    const requestedView = new URLSearchParams(window.location.search).get('view');
    return ['map', 'races', 'run'].includes(requestedView) ? requestedView : 'races';
  });

  return (
    <ErrorBoundary fallbackMessage="The application encountered an error. Please refresh the page.">
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary fallbackMessage="Unable to load the application layout.">
          <div className="h-screen bg-slate-900 flex flex-col overflow-hidden">
            <ErrorBoundary fallbackMessage="Unable to load the header.">
              <Header />
            </ErrorBoundary>

            <ErrorBoundary fallbackMessage="Unable to load statistics.">
              <StatsStrip onStatClick={() => setActiveTab('races')} />
            </ErrorBoundary>

            <main className="flex-1 pb-20 overflow-y-auto">
              <ErrorBoundary fallbackMessage="Unable to load this tab content.">
                <div className={activeTab === 'map' ? 'h-full' : ''}>
                  {activeTab === 'map' && <MapTab />}
                  {activeTab === 'races' && <>
                    <section className="mx-4 my-4 rounded-xl border border-slate-700 bg-slate-800 p-4 text-slate-100">
                      <h1 className="text-xl font-semibold">Explore the races behind the map.</h1>
                      <p className="mt-2 text-sm text-slate-300">Browse district analysis below, or switch to the map for a geographic view. This is an independent analysis tool, not official election results.</p>
                      <button type="button" onClick={() => setActiveTab('map')} className="mt-3 rounded-lg bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">Explore the district map</button>
                    </section>
                    <RacesTab />
                  </>}
                  {activeTab === 'run' && <RunTab />}
                </div>
              </ErrorBoundary>
            </main>

            <ErrorBoundary fallbackMessage="Unable to load navigation.">
              <BottomTabs active={activeTab} onChange={setActiveTab} />
            </ErrorBoundary>
          </div>
        </ErrorBoundary>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
